import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Storefront, Plus, Pencil, Trash, UploadSimple, X, Image as ImageIcon } from "@phosphor-icons/react";
import { api, API } from "@/lib/api";

const COUNTRIES = ["Canada", "UAE", "Saudi Arabia", "Indonesia", "India", "Argentina", "Other"];

const EMPTY = {
  name: "", slug: "", tagline: "", description: "",
  industry: "", countries: [], currency: "USD",
  investment_min: 0, investment_max: 0, franchise_fee: 0, royalty_pct: 0,
  year_founded: 0, units_operating: 0,
  logo_url: "", cover_image_url: "", brochure_url: "",
  display_order: 100, active: true,
};

export default function AdminFranchiseOpps() {
  const [opps, setOpps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/franchise-opps");
      setOpps(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const del = async (opp) => {
    if (!window.confirm(`Delete "${opp.name}"?`)) return;
    try {
      await api.delete(`/admin/franchise-opps/${opp.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-franchise-opps">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <Storefront size={14} weight="duotone" /> Franchise Opportunities
          </div>
          <h2 className="font-display text-2xl text-gos-charcoal">{opps.length} brand{opps.length === 1 ? "" : "s"}</h2>
        </div>
        <button onClick={() => setEditing(EMPTY)} data-testid="opp-new-btn" className="btn-primary text-xs !py-2">
          <Plus size={14} weight="bold" /> Add Opportunity
        </button>
      </div>

      <div className="bg-white border border-gos-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gos-subtle text-xs uppercase tracking-widest text-gos-navy">
            <tr>
              <th className="text-left p-3">Brand</th>
              <th className="text-left p-3">Industry</th>
              <th className="text-left p-3">Countries</th>
              <th className="text-left p-3">Investment</th>
              <th className="text-left p-3">Active</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-gos-muted">Loading…</td></tr>}
            {!loading && opps.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-gos-muted">No opportunities yet. Add one to get started.</td></tr>
            )}
            {opps.map((o) => (
              <tr key={o.id} data-testid={`opp-row-${o.slug}`} className="border-t border-gos-border hover:bg-gos-subtle/30">
                <td className="p-3">
                  <div className="text-gos-charcoal font-medium">{o.name}</div>
                  <div className="text-xs text-gos-muted">/{o.slug}</div>
                </td>
                <td className="p-3 text-xs text-gos-slate">{o.industry || "—"}</td>
                <td className="p-3 text-xs text-gos-slate">{(o.countries || []).join(", ") || "—"}</td>
                <td className="p-3 text-xs text-gos-slate">
                  {(o.investment_min || o.investment_max)
                    ? `${o.currency} ${Number(o.investment_min).toLocaleString()}–${Number(o.investment_max).toLocaleString()}`
                    : "—"}
                </td>
                <td className="p-3"><Badge active={o.active} /></td>
                <td className="p-3 text-right">
                  <button onClick={() => setEditing(o)} data-testid={`opp-edit-${o.slug}`} className="text-gos-navy hover:opacity-70 text-xs inline-flex items-center gap-1 mr-3"><Pencil size={14} /> Edit</button>
                  <button onClick={() => del(o)} data-testid={`opp-del-${o.slug}`} className="text-rose-600 hover:opacity-70 text-xs inline-flex items-center gap-1"><Trash size={14} /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <OppEditor opp={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
      )}
    </div>
  );
}

function Badge({ active }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm ${active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
      {active ? "active" : "hidden"}
    </span>
  );
}

function OppEditor({ opp, onClose, onSaved }) {
  const isNew = !opp.id;
  const [form, setForm] = useState({ ...opp, countries: opp.countries || [] });
  const [saving, setSaving] = useState(false);
  const [mapFile, setMapFile] = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCountry = (c) => upd("countries", form.countries.includes(c)
    ? form.countries.filter((x) => x !== c)
    : [...form.countries, c]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      ["investment_min", "investment_max", "franchise_fee", "royalty_pct", "year_founded", "units_operating", "display_order"].forEach((k) => {
        payload[k] = Number(payload[k] || 0);
      });
      let saved;
      if (isNew) {
        const r = await api.post("/admin/franchise-opps", payload);
        saved = r.data;
      } else {
        const r = await api.patch(`/admin/franchise-opps/${opp.id}`, payload);
        saved = r.data;
      }
      if (mapFile) {
        const fd = new FormData(); fd.append("file", mapFile);
        await api.post(`/admin/franchise-opps/${saved.id}/territory-map`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(isNew ? "Created" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end" onClick={onClose} data-testid="opp-editor">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gos-border p-5 flex items-center justify-between z-10">
          <div>
            <div className="overline">{isNew ? "New opportunity" : "Edit opportunity"}</div>
            <h3 className="font-display text-2xl text-gos-charcoal">{form.name || "Untitled"}</h3>
          </div>
          <button onClick={onClose} data-testid="opp-editor-close" className="text-gos-slate hover:text-gos-navy"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5">
          <Field label="Brand name *" value={form.name} onChange={(v) => upd("name", v)} testid="opp-name" />
          <Field label="Slug (URL)" value={form.slug} onChange={(v) => upd("slug", v)} placeholder="auto-generated from name" testid="opp-slug" />
          <Field label="Tagline" value={form.tagline} onChange={(v) => upd("tagline", v)} testid="opp-tagline" />
          <Textarea label="Description" value={form.description} onChange={(v) => upd("description", v)} testid="opp-description" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Industry" value={form.industry} onChange={(v) => upd("industry", v)} testid="opp-industry" />
            <Field label="Currency" value={form.currency} onChange={(v) => upd("currency", v)} testid="opp-currency" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-gos-muted mb-1">Available countries</label>
            <div className="flex flex-wrap gap-2" data-testid="opp-countries">
              {COUNTRIES.map((c) => (
                <button key={c} type="button" onClick={() => toggleCountry(c)}
                  data-testid={`opp-country-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 py-1 text-xs uppercase tracking-widest rounded-sm border ${form.countries.includes(c) ? "bg-gos-navy text-white border-gos-navy" : "border-gos-border text-gos-slate"}`}
                >{c}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Investment min" value={form.investment_min} onChange={(v) => upd("investment_min", v)} testid="opp-inv-min" />
            <NumField label="Investment max" value={form.investment_max} onChange={(v) => upd("investment_max", v)} testid="opp-inv-max" />
            <NumField label="Franchise fee" value={form.franchise_fee} onChange={(v) => upd("franchise_fee", v)} testid="opp-fee" />
            <NumField label="Royalty %" value={form.royalty_pct} onChange={(v) => upd("royalty_pct", v)} testid="opp-royalty" />
            <NumField label="Year founded" value={form.year_founded} onChange={(v) => upd("year_founded", v)} testid="opp-year" />
            <NumField label="Units operating" value={form.units_operating} onChange={(v) => upd("units_operating", v)} testid="opp-units" />
          </div>
          <Field label="Logo URL" value={form.logo_url} onChange={(v) => upd("logo_url", v)} testid="opp-logo-url" />
          <Field label="Cover image URL" value={form.cover_image_url} onChange={(v) => upd("cover_image_url", v)} testid="opp-cover-url" />
          <Field label="Brochure URL (PDF, public link)" value={form.brochure_url} onChange={(v) => upd("brochure_url", v)} testid="opp-brochure-url" />

          {/* Territory map */}
          <div>
            <label className="block text-xs uppercase tracking-widest text-gos-muted mb-1">Territory map</label>
            {opp.territory_map_mime && (
              <img src={`${API}/franchise/opportunities/${opp.slug}/territory-map`} alt="Territory map" className="w-full max-h-48 object-contain border border-gos-border rounded-sm mb-2" />
            )}
            <label className="flex items-center gap-3 border border-dashed border-gos-border rounded-sm px-3 py-3 text-sm cursor-pointer hover:border-gos-navy">
              <UploadSimple size={18} weight="duotone" className="text-gos-navy" />
              <span className="flex-1 truncate">{mapFile ? mapFile.name : "Upload territory map (PNG/JPG, up to 4 MB)"}</span>
              <input data-testid="opp-map-upload" type="file" accept="image/*" className="hidden"
                onChange={(e) => setMapFile(e.target.files?.[0] || null)} />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <NumField label="Display order" value={form.display_order} onChange={(v) => upd("display_order", v)} testid="opp-display-order" />
            <div>
              <label className="block text-xs uppercase tracking-widest text-gos-muted mb-1">Status</label>
              <button type="button" onClick={() => upd("active", !form.active)} data-testid="opp-active-toggle"
                className={`px-4 py-2 text-xs uppercase tracking-widest rounded-sm border w-full ${form.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                {form.active ? "Active" : "Hidden"}
              </button>
            </div>
          </div>

          <div className="flex gap-2 pt-4 border-t border-gos-border">
            <button onClick={save} disabled={saving} data-testid="opp-save-btn" className="btn-primary flex-1">
              {saving ? "Saving…" : (isNew ? "Create opportunity" : "Save changes")}
            </button>
            <button onClick={onClose} className="btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-gos-muted mb-1">{label}</label>
      <input data-testid={testid} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm" />
    </div>
  );
}
function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-gos-muted mb-1">{label}</label>
      <input data-testid={testid} type="number" min="0" value={value || 0} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm" />
    </div>
  );
}
function Textarea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-gos-muted mb-1">{label}</label>
      <textarea data-testid={testid} value={value || ""} rows={4} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm" />
    </div>
  );
}
